import React, { FC, PropsWithChildren, ReactNode, useEffect } from 'react';
import { generatePath, Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { CspPath } from '../../../CspPath';
import { useNavigationContext } from '../context/useNavigationContextState';
import { useMatchPaths } from '../hooks/useMatchPaths';
import { InformedConsentConfigContextContent } from '../../informed-consent/context/InformedConsentConfigContextContent';
import { PathUtil } from '../util/PathUtil';
import { useCanListStudyIcfs } from '../../informed-consent/hook/useCanListStudyIcfs';

type CanListStudyIcfsGuardProps = PropsWithChildren<{
  fallbackElement: ReactNode;
}>;

const CanListStudyIcfsGuard: FC<CanListStudyIcfsGuardProps> = ({ children, fallbackElement }) => {
  const canListStudyIcfs = useCanListStudyIcfs();

  if (canListStudyIcfs) {
    return children;
  } else {
    return fallbackElement;
  }
};

const toRelativePath = (absolutePath: CspPath): string =>
  PathUtil.toRelativePath(CspPath.INFORMED_CONSENT, absolutePath, true);

const InformedConsentRoot: FC = () => {
  const { setSelectedSubPath } = useNavigationContext();

  const matchedCspPath = useMatchPaths([
    CspPath.INFORMED_CONSENT_LIST_MASTER,
    CspPath.INFORMED_CONSENT_LIST_LOCAL,
    CspPath.INFORMED_CONSENT_LIST_SITE,
  ]);

  useEffect(() => {
    if (matchedCspPath) {
      setSelectedSubPath(CspPath.INFORMED_CONSENT, matchedCspPath);
    }
  }, [matchedCspPath, setSelectedSubPath]);

  return (
    <InformedConsentConfigContextContent>
      <Outlet />
    </InformedConsentConfigContextContent>
  );
};

const InformedConsentListMasterContainer = React.lazy(
  () =>
    import(
      /* webpackPrefetch: true */ '../../informed-consent/informed-consent-list-master/container/InformedConsentListMasterContainer'
    ),
);

const InformedConsentListLocalContainer = React.lazy(
  () =>
    import(
      /* webpackPrefetch: true */ '../../informed-consent/informed-consent-list-local/container/InformedConsentListLocalContainer'
    ),
);

const InformedConsentListSiteContainer = React.lazy(
  () =>
    import(
      /* webpackPrefetch: true */ '../../informed-consent/informed-consent-list-site/container/InformedConsentListSiteContainer'
    ),
);

export const InformedConsentRoutes: RouteObject[] = [
  {
    element: <InformedConsentRoot />,
    children: [
      {
        path: toRelativePath(CspPath.INFORMED_CONSENT),
        element: (
          <CanListStudyIcfsGuard
            // At this point, the only role that doesn't have access to the
            // Study list is the PI (who has access to the Local list)
            fallbackElement={<Navigate replace to={generatePath(CspPath.INFORMED_CONSENT_LIST_LOCAL)} />}
          >
            <InformedConsentListMasterContainer />
          </CanListStudyIcfsGuard>
        ),
      },
      {
        path: toRelativePath(CspPath.INFORMED_CONSENT_LIST_MASTER),
        element: <InformedConsentListMasterContainer />,
      },
      {
        path: toRelativePath(CspPath.INFORMED_CONSENT_LIST_SITE),
        element: <InformedConsentListSiteContainer />,
      },
      {
        path: toRelativePath(CspPath.INFORMED_CONSENT_LIST_LOCAL),
        element: <InformedConsentListLocalContainer />,
      },
      {
        path: '*',
        element: <InformedConsentListMasterContainer />,
      },
    ],
  },
];
